import React from 'react';
import { Title, Text, Card, Group, Badge, SimpleGrid, Loader, Stack } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../components/ui/PageContainer';
import { useTranslation } from 'react-i18next';
import { useUserCompetencyMap } from '../../hooks/useApi';
import { useCompetencyMapDirectory } from '../../hooks/useCompetencyApi';

// Overview of every class's competency map (tester, devops, developer, …). Your
// own class is highlighted with your current grade; clicking any card opens that
// class's map. Read-only for non-admins — mutations are hidden on the map page.
export const CompetencyMap: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: directory = [], isLoading } = useCompetencyMapDirectory();
  const { data: ownMap, currentGrade } = useUserCompetencyMap();
  const ownMapId = ownMap?._id;

  // Your own class first, then the rest in their natural order.
  const orderedMaps = [...directory].sort(
    (a, b) => (b._id === ownMapId ? 1 : 0) - (a._id === ownMapId ? 1 : 0),
  );

  return (
    <PageContainer>
      <Stack gap={4} mb="lg">
        <Title order={2}>{t('competencyMaps.title')}</Title>
        <Text c="dimmed" size="sm">
          {t('competencyMaps.pickClassHint')}
        </Text>
      </Stack>

      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : orderedMaps.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {t('competencyMaps.noMapsYet')}
        </Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" style={{ maxWidth: '100%' }}>
          {orderedMaps.map((m) => {
            const isOwn = m._id === ownMapId;
            const professionsLabel = m.professions?.map((p) => p.name).join(', ');
            return (
              <Card
                key={m._id}
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                className="review-card"
                style={{
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: isOwn ? 'var(--mantine-color-teal-filled)' : undefined,
                  background: isOwn ? 'var(--mantine-color-teal-light)' : undefined,
                }}
                onClick={() => navigate(`/competency-maps/${m._id}`)}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap" mb="xs">
                  <Text fw={600} size="md" lineClamp={2} style={{ flex: 1, minWidth: 0 }}>
                    {m.name}
                  </Text>
                  {isOwn ? (
                    <Badge color="teal" variant="filled" size="sm" style={{ flexShrink: 0 }}>
                      {t('competencyMaps.yourClass')}
                    </Badge>
                  ) : (
                    <IconArrowRight
                      size={16}
                      style={{
                        color: 'var(--mantine-color-dimmed)',
                        flexShrink: 0,
                        marginTop: 3,
                      }}
                    />
                  )}
                </Group>

                {isOwn && currentGrade?.title && (
                  <Badge
                    color="teal"
                    variant="light"
                    size="sm"
                    mb="xs"
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {t('competencyMaps.currentGrade')}: {currentGrade.title}
                  </Badge>
                )}

                <div style={{ flex: 1 }}>
                  {professionsLabel && (
                    <Text size="xs" c="dimmed">
                      {professionsLabel}
                    </Text>
                  )}
                  {m.grades && m.grades.length > 0 && (
                    <Text size="xs" c="dimmed" mt={6}>
                      {t('competencyMaps.grades')}: {m.grades.map((g) => g.title).join(', ')}
                    </Text>
                  )}
                </div>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </PageContainer>
  );
};
